import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Download, FileText, Lock, AlertCircle } from "lucide-react";
import { motion } from "framer-motion";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const STATUS_LABELS = { draft: "BORRADOR", sent: "ENVIADA", partial: "PARCIAL", paid: "PAGADA", overdue: "VENCIDA", cancelled: "CANCELADA" };
const STATUS_COLOR = { paid: "#10B981", partial: "#F59E0B", overdue: "#EF4444", draft: "#71717A", sent: "#3B82F6", cancelled: "#52525B" };

export default function PublicInvoicePage() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${BACKEND}/api/factura-publica/${id}?token=${encodeURIComponent(token)}`)
      .then(async (r) => {
        if (r.status === 403) { setError("El link es inválido o fue revocado."); return null; }
        if (r.status === 410) { setError("El link ha expirado. Pedile uno nuevo a tu contacto comercial."); return null; }
        if (!r.ok) { setError("Error al cargar la factura"); return null; }
        return r.json();
      })
      .then((d) => d && setData(d))
      .catch(() => setError("Error de red"));
  }, [id, token]);

  const downloadPdf = () => {
    window.open(`${BACKEND}/api/factura-publica/${id}/pdf?token=${encodeURIComponent(token)}`, "_blank");
  };

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="border border-destructive/40 bg-destructive/5 rounded-sm p-8 max-w-md text-center" data-testid="public-invoice-error">
          <AlertCircle className="w-10 h-10 text-destructive mx-auto mb-3" />
          <h1 className="font-display font-black text-2xl mb-2">No disponible</h1>
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground">Cargando...</div>;

  const { invoice: inv, business: biz } = data;
  const total = Number(inv.total) || 0;
  const paid = Number(inv.paid_total) || 0;
  const pending = Math.max(0, total - paid);

  return (
    <div className="min-h-screen bg-background py-10 px-4" data-testid="public-invoice">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl mx-auto bg-secondary/5 border border-border rounded-sm overflow-hidden">
        {/* Header */}
        <div className="p-6 lg:p-8 border-b-2 border-primary flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter text-primary">FACTURA</h1>
            <p className="font-mono text-sm font-bold mt-2">N° {inv.code}</p>
            <span className="inline-block mt-3 px-3 py-1 text-xs font-bold tracking-widest uppercase rounded-sm" style={{ background: STATUS_COLOR[inv.status], color: "white" }} data-testid="public-invoice-status">
              {STATUS_LABELS[inv.status] || inv.status}
            </span>
          </div>
          <div className="text-right text-sm">
            {biz.logo && <img src={biz.logo.startsWith("/") ? `${BACKEND}${biz.logo}` : biz.logo} alt="logo" className="max-h-12 mb-2 ml-auto object-contain" />}
            <p className="font-display font-bold text-base">{biz.name}</p>
            {biz.nit && <p className="text-xs text-muted-foreground">NIT: {biz.nit}</p>}
            {biz.address && <p className="text-xs text-muted-foreground">{biz.address}</p>}
            <p className="text-xs text-muted-foreground">{biz.phone} {biz.email && `· ${biz.email}`}</p>
          </div>
        </div>

        {/* Meta */}
        <div className="p-6 lg:p-8 grid grid-cols-2 md:grid-cols-4 gap-4 border-b border-border">
          <Meta label="Cliente" value={inv.client_name || "—"} />
          <Meta label="Emisión" value={inv.issue_date || "—"} />
          <Meta label="Vencimiento" value={inv.due_date || "—"} />
          <Meta label="Moneda" value={inv.currency} />
        </div>

        {/* Items */}
        <div className="p-6 lg:p-8">
          <table className="w-full text-sm">
            <thead className="bg-secondary/30 text-xs uppercase tracking-widest">
              <tr><th className="text-left p-3">Detalle</th><th className="text-right p-3">Cant.</th><th className="text-right p-3">P. unit.</th><th className="text-right p-3">Subtotal</th></tr>
            </thead>
            <tbody>
              {(inv.items || []).map((it, i) => (
                <tr key={i} className="border-t border-border">
                  <td className="p-3">{it.description}</td>
                  <td className="p-3 text-right">{it.qty}</td>
                  <td className="p-3 text-right">{Number(it.unit_price).toFixed(2)}</td>
                  <td className="p-3 text-right font-bold">{Number(it.total).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="p-6 lg:p-8 flex justify-end">
          <table className="w-full md:w-1/2 text-sm">
            <tbody>
              <tr><td className="p-2">Subtotal</td><td className="p-2 text-right">{inv.currency} {Number(inv.subtotal).toLocaleString()}</td></tr>
              <tr><td className="p-2">Impuesto ({Number(inv.tax_pct).toFixed(1)}%)</td><td className="p-2 text-right">{inv.currency} {Number(inv.tax_amount).toLocaleString()}</td></tr>
              <tr className="bg-primary text-primary-foreground"><td className="p-3 font-display font-black text-lg">TOTAL</td><td className="p-3 text-right font-display font-black text-lg">{inv.currency} {total.toLocaleString()}</td></tr>
              {paid > 0 && <tr><td className="p-2">Cobrado</td><td className="p-2 text-right text-emerald-500 font-bold">{inv.currency} {paid.toLocaleString()}</td></tr>}
              {pending > 0 && paid > 0 && <tr><td className="p-2 font-bold">Saldo pendiente</td><td className="p-2 text-right font-bold text-amber-500">{inv.currency} {pending.toLocaleString()}</td></tr>}
            </tbody>
          </table>
        </div>

        {/* Payments + terms */}
        {(inv.payments || []).length > 0 && (
          <div className="p-6 lg:p-8 border-t border-border">
            <h3 className="text-xs uppercase tracking-widest font-bold mb-3 text-muted-foreground">Pagos registrados</h3>
            <ul className="space-y-1 text-sm">
              {inv.payments.map((p, i) => (
                <li key={i} className="flex gap-3">
                  <span className="text-muted-foreground w-24">{(p.date || "").slice(0, 10)}</span>
                  <span className="flex-1">{p.method} {p.reference && <span className="italic text-muted-foreground">· {p.reference}</span>}</span>
                  <span className="font-bold text-emerald-500">{inv.currency} {Number(p.amount).toLocaleString()}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="p-6 lg:p-8 bg-secondary/20 border-t border-border">
          <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2">Términos de pago</p>
          <p className="text-sm">{inv.payment_terms || "—"}</p>
          {inv.notes && <p className="text-xs italic mt-3 text-muted-foreground">{inv.notes}</p>}
        </div>

        <div className="p-6 lg:p-8 border-t border-border flex flex-wrap gap-3 items-center justify-between">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Lock className="w-3 h-3" /> Link seguro · solo accesible con tu token</p>
          <button onClick={downloadPdf} data-testid="public-download-pdf" className="inline-flex items-center gap-2 px-5 h-11 bg-primary text-primary-foreground rounded-sm font-semibold text-sm">
            <Download className="w-4 h-4" /> Descargar PDF
          </button>
        </div>
      </motion.div>
      <p className="text-center text-[10px] text-muted-foreground mt-6">Generado por INFLUCAST · ¿Dudas? Escribinos.</p>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{label}</p>
      <p className="font-display font-bold text-sm">{value}</p>
    </div>
  );
}
